/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Box,
  Breadcrumbs,
  FormControl,
  InputAdornment,
  IconButton,
  Select,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { UpdateButton } from "../../../components/update_button";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { PerPage } from "../../../constants/perPage";
import { formatDate } from "../../../utils/formatDate";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { useDebounce } from "../../../utils/useDebounce";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";
import {
  ALL_FILTER_VALUE,
  CLASSIFIED_DISCOUNT_DATE_FILTERS,
  buildClassifiedDiscountDrilldownSearch,
  CLASSIFIED_DISCOUNT_REPORT_PATH,
  getClassifiedDiscountApiFilters,
  parseClassifiedDiscountDrilldownSearch,
} from "../../../utils/classifiedDiscountDrilldown";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  datePickerStyles,
  selectBaseStyles,
  viewTypographyStyles,
} from "./styles";
import { OrderNumberCell } from "../../../components/order_number_cell";
import { OrderAppliedDiscountModal } from "../../../components/order_discount_modal";

const defaultFilters = {
  dateFilter: "Last 7 Days",
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
  customDateFrom: dayjs().subtract(7, "day"),
  customDateTo: dayjs(),
};

const STATUS_LABELS = {
  managed: "Managed",
  unmanaged: "Unmanaged",
  unclassified: "Unclassified",
  unmatched: "Unmatched",
};

const COLUMN_LABELS = {
  discount_name: "Perq Name",
  discount_reason: "Perq Reason",
  discount_comment: "Perq Comment",
  discount_amount: "Perq Amount",
};

const getGuestName = (networkGuest) => {
  if (!networkGuest?.first_name && !networkGuest?.last_name) return "-";

  return [networkGuest?.first_name, networkGuest?.last_name]
    .filter(Boolean)
    .join(" ");
};

const resolveIncomingDateFilter = (dateFilter) =>
  CLASSIFIED_DISCOUNT_DATE_FILTERS.includes(dateFilter)
    ? dateFilter
    : "Custom Date";

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  if (
    typeof value?.isValid === "function" &&
    typeof value?.format === "function" &&
    typeof value?.clone === "function"
  ) {
    return value;
  }

  const sourceValue = value?.$d || value;
  const parsedValue = dayjs(sourceValue);
  return parsedValue.isValid() ? parsedValue : null;
};

const resolveInitialFilters = (navigationFilters, parsedFilters) => {
  const incomingFilters = navigationFilters || {};
  const resolvedDateFilter = resolveIncomingDateFilter(
    incomingFilters.dateFilter || parsedFilters.dateFilter,
  );

  const incomingDateFrom = normalizeDayjsValue(incomingFilters.customDateFrom);
  const incomingDateTo = normalizeDayjsValue(incomingFilters.customDateTo);
  const parsedDateFrom = normalizeDayjsValue(parsedFilters.customDateFrom);
  const parsedDateTo = normalizeDayjsValue(parsedFilters.customDateTo);

  return {
    ...defaultFilters,
    ...parsedFilters,
    ...incomingFilters,
    dateFilter: resolvedDateFilter,
    customDateFrom:
      resolvedDateFilter === "Custom Date"
        ? incomingDateFrom || parsedDateFrom || null
        : incomingDateFrom || parsedDateFrom || defaultFilters.customDateFrom,
    customDateTo:
      resolvedDateFilter === "Custom Date"
        ? incomingDateTo || parsedDateTo || null
        : incomingDateTo || parsedDateTo || defaultFilters.customDateTo,
  };
};

export const ClassifiedDiscountRecords = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showNotification = useNotificationContext();
  const navigationFilters = location.state?.classifiedDiscountFilters;

  const parsedSearchState = useMemo(
    () => parseClassifiedDiscountDrilldownSearch(location.search),
    [location.search],
  );

  const initialNavigationFiltersRef = useRef(navigationFilters || null);
  const initialSearchStateRef = useRef(parsedSearchState);
  const initialStatusRef = useRef(
    initialNavigationFiltersRef.current?.status || initialSearchStateRef.current.status,
  );
  const initialFiltersRef = useRef(
    resolveInitialFilters(
      initialNavigationFiltersRef.current,
      initialSearchStateRef.current.filters,
    ),
  );

  const initialStatus = initialStatusRef.current;
  const initialFilters = initialFiltersRef.current;

  const [status, setStatus] = useState(initialStatus);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("order_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const synchronizedFiltersRef = useRef(null);
  const hydratedSearchRef = useRef(null);
  const invalidStatusToastRef = useRef(null);
  const pendingRestaurantAutoSelectFromGroupRef = useRef(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const navigationFiltersSignature = useMemo(
    () => JSON.stringify(navigationFilters || null),
    [navigationFilters],
  );
  const isUnmatchedView = status === "managed" && appliedFilters?.has_ot_discount === false;
  const statusLabel = isUnmatchedView
    ? STATUS_LABELS.unmatched
    : STATUS_LABELS[status] || STATUS_LABELS.unmanaged;

  useEffect(() => {
    const syncSignature = `${location.search}|${navigationFiltersSignature}`;

    if (synchronizedFiltersRef.current === syncSignature) return;

    const nextStatus = navigationFilters?.status || parsedSearchState.status;
    const nextFilters = resolveInitialFilters(
      navigationFilters,
      parsedSearchState.filters,
    );

    synchronizedFiltersRef.current = syncSignature;

    setStatus(nextStatus);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setFiltersChanged(false);
    setPage(1);

    const normalizedSearch = buildClassifiedDiscountDrilldownSearch({
      status: nextStatus,
      restaurantGroupId: nextFilters.restaurant_group_id,
      restaurantIds: nextFilters.restaurant_ids,
      dateFilter: nextFilters.dateFilter,
      customDateFrom: nextFilters.customDateFrom,
      customDateTo: nextFilters.customDateTo,
      hasOtDiscount: nextFilters.has_ot_discount,
    });

    const shouldSyncSearch = normalizedSearch !== location.search;

    if (shouldSyncSearch) {
      navigate(`${CLASSIFIED_DISCOUNT_REPORT_PATH}${normalizedSearch}`, {
        replace: true,
        state: location.state,
      });
    }

    if (!navigationFilters && parsedSearchState.hadInvalidStatus) {
      if (invalidStatusToastRef.current !== location.search) {
        showNotification(
          "Invalid perq status. Showing managed records instead.",
          "warning",
        );
        invalidStatusToastRef.current = location.search;
      }
    }
  }, [
    location.search,
    navigate,
    navigationFilters,
    navigationFiltersSignature,
    parsedSearchState.filters,
    parsedSearchState.hadInvalidStatus,
    parsedSearchState.status,
    showNotification,
  ]);

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get(
      "/v1/admin/dropdowns/restaurant_groups_list",
      {
        params: {
          page: pageParam,
          per_page: PerPage,
          search: debouncedRestaurantGroupSearch || undefined,
        },
      },
    );

    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: "classified_discount_records",
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids:
          filters?.restaurant_group_id === ALL_FILTER_VALUE
            ? undefined
            : filters?.restaurant_group_id,
      },
    });

    return response?.data?.map((record) => ({
      ...normalizeRestaurant(record),
      id: Number(record?.value ?? record?.id),
    })) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
      source: "classified_discount_records",
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const fetchClassifiedDiscountRecords = async () => {
    const response = await api.get(
      "/v1/admin/reports/classified_discount_records",
      {
        params: {
          status,
          page,
          per_page: itemsPerPage,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...getClassifiedDiscountApiFilters(appliedFilters),
        },
      },
    );

    return {
      data: response?.data,
      pageCount: Number(response?.headers["total-count"] || 0),
    };
  };

  const {
    data: classifiedDiscounts,
    isFetching,
    error,
  } = useQuery({
    queryKey: Query_Keys.classified_discount_records({
      status,
      queryFilter: appliedFilters,
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchClassifiedDiscountRecords,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: filtersHydrated,
  });

  const fetchOrderData = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrderId}/detail`);
    return response?.data;
  }

  const { data: orderData, isLoading: orderDataLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({
      selected_order: selectedOrderId,
    }),
    queryFn: fetchOrderData,
    enabled: !!selectedOrderId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, "label", ["value"]));
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, "id", ["name"]));
  }, [restaurants]);

  useEffect(() => {
    if (restaurantGroupFetching || restaurantFetching) return;
    if (hydratedSearchRef.current === location.search) return;

    let cancelled = false;

    const hydrateFilters = async () => {
      const nextFilters = resolveInitialFilters(
        navigationFilters,
        parsedSearchState.filters,
      );

      const hasExplicitRestaurantGroupSelection =
        nextFilters.restaurant_group_id !== ALL_FILTER_VALUE;

      const hasExplicitRestaurantSelection = Array.isArray(nextFilters.restaurant_ids)
        && nextFilters.restaurant_ids.some((restaurantId) => restaurantId !== ALL_FILTER_VALUE);

      if (
        !hasExplicitRestaurantGroupSelection
        && !debouncedRestaurantGroupSearch
        && !hasNextRestaurantGroupsPage
        && hasSingleItem(restaurantGroups)
      ) {
        nextFilters.restaurant_group_id = getSingleItemValue(restaurantGroups)?.label;
      }

      if (
        !hasExplicitRestaurantSelection
        && !debouncedRestaurantSearch
        && !hasNextRestaurantsPage
        && hasSingleItem(restaurants)
      ) {
        nextFilters.restaurant_ids = [getSingleItemValue(restaurants)?.id];
      }

      const selectedRestaurantGroupId =
        nextFilters.restaurant_group_id !== ALL_FILTER_VALUE
          ? nextFilters.restaurant_group_id
          : null;

      const selectedRestaurantIds = Array.isArray(nextFilters.restaurant_ids)
        ? nextFilters.restaurant_ids.filter((restaurantId) => restaurantId !== ALL_FILTER_VALUE)
        : [];

      const missingRestaurantGroupPromise =
        selectedRestaurantGroupId &&
        !restaurantGroups.some((restaurantGroup) => `${restaurantGroup?.label}` === `${selectedRestaurantGroupId}`)
          ? api.get(`/v1/admin/restaurant_groups/${selectedRestaurantGroupId}`)
              .then((response) => ({
                label: response?.data?.id ?? selectedRestaurantGroupId,
                value: response?.data?.name,
              }))
              .catch(() => null)
          : Promise.resolve(null);

      const missingRestaurantPromises = selectedRestaurantIds
        .filter((restaurantId) => !restaurants.some((restaurant) => `${restaurant?.id}` === `${restaurantId}`))
        .map((restaurantId) => (
          api.get(`/v1/admin/restaurants/${restaurantId}`)
            .then((response) => ({
              id: Number(response?.data?.id ?? restaurantId),
              name: response?.data?.name,
            }))
            .catch(() => null)
        ));

      const [missingRestaurantGroup, missingRestaurants] = await Promise.all([
        missingRestaurantGroupPromise,
        Promise.all(missingRestaurantPromises),
      ]);

      if (cancelled) return;

      if (missingRestaurantGroup?.value) {
        setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, [missingRestaurantGroup], "label"));
      }

      const validMissingRestaurants = missingRestaurants.filter((restaurant) => restaurant?.name);

      if (validMissingRestaurants.length > 0) {
        setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, validMissingRestaurants, "id"));
      }

      setStatus(navigationFilters?.status || parsedSearchState.status);
      setFilters(nextFilters);
      setAppliedFilters(nextFilters);
      setFiltersChanged(false);
      setFiltersHydrated(true);
      hydratedSearchRef.current = location.search;
    };

    hydrateFilters();

    return () => {
      cancelled = true;
    };
    // Keep the live dropdown collections in the dep list because this effect both
    // auto-selects true single-option access and hydrates missing labels from the
    // currently loaded pages. The early returns above make the repeated object
    // identity churn from useInfiniteQuery/select harmless.
  }, [
    debouncedRestaurantGroupSearch,
    debouncedRestaurantSearch,
    hasNextRestaurantGroupsPage,
    hasNextRestaurantsPage,
    location.search,
    navigationFilters,
    parsedSearchState.filters,
    parsedSearchState.status,
    restaurantGroups,
    restaurantGroupFetching,
    restaurants,
    restaurantFetching,
  ]);

  useEffect(() => {
    if (!error) return;

    const message = (
      error?.response?.data?.error || "Failed to load classified perqs."
    ).replace(/classified discounts/gi, "classified perqs");
    showNotification(message, "error");
  }, [error, showNotification]);

  useEffect(() => {
    if (!pendingRestaurantAutoSelectFromGroupRef.current) return;

    if (filters?.restaurant_group_id === ALL_FILTER_VALUE) {
      pendingRestaurantAutoSelectFromGroupRef.current = false;
      return;
    }

    if (debouncedRestaurantSearch || restaurantFetching || restaurantsFetchingNextPage) {
      return;
    }

    if (!hasNextRestaurantsPage && hasSingleItem(restaurants)) {
      const nextRestaurantId = getSingleItemValue(restaurants)?.id;

      setFilters((prev) => {
        const alreadySelected = prev?.restaurant_ids?.length === 1
          && `${prev?.restaurant_ids?.[0]}` === `${nextRestaurantId}`;

        if (alreadySelected) {
          return prev;
        }

        return {
          ...prev,
          restaurant_ids: [nextRestaurantId],
        };
      });
    }

    pendingRestaurantAutoSelectFromGroupRef.current = false;
  }, [
    debouncedRestaurantSearch,
    filters?.restaurant_group_id,
    hasNextRestaurantsPage,
    restaurants,
    restaurantsFetchingNextPage,
    restaurantFetching,
  ]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (restaurantGroup) => `${restaurantGroup?.label}` === `${filters?.restaurant_group_id}`,
  );

  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) => (
    filters?.restaurant_ids?.some((restaurantId) => `${restaurantId}` === `${restaurant?.id}`)
  ));

  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(
        restaurants,
        selectedRestaurantOptions,
        "id",
      );

  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All Restaurant Groups";

    const cachedRestaurantGroup = restaurantGroupOptionsCache?.find(
      (record) => `${record?.label}` === `${id}`,
    );

    if (cachedRestaurantGroup?.value) {
      return cachedRestaurantGroup.value;
    }

    const restaurantGroup = restaurantGroups?.find(
      (record) => `${record.label}` === `${id}`,
    );
    return restaurantGroup?.value || "All Restaurant Groups";
  };

  const getRestaurantDisplayValue = () => {
    if (
      !filters?.restaurant_ids?.length ||
      filters.restaurant_ids.includes(ALL_FILTER_VALUE)
    ) {
      return "All Restaurants";
    }

    if (filters.restaurant_ids.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(
        (record) => `${record.id}` === `${filters.restaurant_ids[0]}`,
      );
      return restaurant?.name || "All Restaurants";
    }

    return `${filters.restaurant_ids.length} Restaurants Selected`;
  };

  const handleRestaurantGroupChange = (event) => {
    pendingRestaurantAutoSelectFromGroupRef.current =
      event.target.value !== ALL_FILTER_VALUE;
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: event.target.value,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    setFiltersChanged(true);
  };

  const handleDateFilterChange = (event) => {
    const nextDateFilter = event.target.value;

    setFilters((prev) => ({
      ...prev,
      dateFilter: nextDateFilter,
      customDateFrom: null,
      customDateTo: null,
    }));
    setFiltersChanged(true);
  };

  const handleRestaurantChange = (event) => {
    const {
      target: { value },
    } = event;

    if (
      (value.length === 1 && value.includes(ALL_FILTER_VALUE)) ||
      value.length === 0
    ) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((record) => record !== ALL_FILTER_VALUE),
      }));
    }

    setFiltersChanged(true);
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => {
      const normalizedValue = normalizeDayjsValue(value);

      if (field === "customDateFrom") {
        if (!normalizedValue) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: normalizedValue,
          customDateTo:
            isValidDayjsValue(normalizedValue) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: normalizedValue };
    });
    setFiltersChanged(true);
  };

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleRestaurantGroupClear = () => {
    pendingRestaurantAutoSelectFromGroupRef.current = false;
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    setFiltersChanged(true);
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    setFiltersChanged(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);

    const search = buildClassifiedDiscountDrilldownSearch({
      status,
      restaurantGroupId: filters.restaurant_group_id,
      restaurantIds: filters.restaurant_ids,
      dateFilter: filters.dateFilter,
      customDateFrom: filters.customDateFrom,
      customDateTo: filters.customDateTo,
      hasOtDiscount: filters.has_ot_discount,
    });

    navigate(`${CLASSIFIED_DISCOUNT_REPORT_PATH}${search}`, {
      replace: true,
      state: {
        classifiedDiscountFilters: {
          status,
          ...filters,
        },
      },
    });
  };

  const handleReset = () => {
    const resetFilters = initialNavigationFiltersRef.current
      ? initialFiltersRef.current
      : defaultFilters;

    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setSortBy("order_date");
    setSortOrder("desc");

    navigate(
      `${CLASSIFIED_DISCOUNT_REPORT_PATH}${buildClassifiedDiscountDrilldownSearch({
        status,
        restaurantGroupId: resetFilters.restaurant_group_id,
        restaurantIds: resetFilters.restaurant_ids,
        dateFilter: resetFilters.dateFilter,
        customDateFrom: resetFilters.customDateFrom,
        customDateTo: resetFilters.customDateTo,
        hasOtDiscount: resetFilters.has_ot_discount,
      })}`,
      {
        replace: true,
        state: {
          classifiedDiscountFilters: {
            status,
            ...resetFilters,
          },
        },
      },
    );
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleOpen = (orderId) => {
    setOrderViewOpen(true);
    setSelectedOrderId(orderId);
  }

  const handleCloseOrderModal = () => {
    setOrderViewOpen(false);
    setSelectedOrderId(null);
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);

      const query = buildQueryParams({
        report: "classified_discount_records",
        status,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...getClassifiedDiscountApiFilters(appliedFilters),
      });

      const { response } = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?${query}`,
        {
          method: "GET",
          defaultErrorMessage: "Failed to download CSV",
        },
      );

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = dayjs().format("YYYY-MM-DD");
      const exportStatus = isUnmatchedView ? "unmatched" : status;

      link.href = downloadUrl;
      link.download = `classified_discount_records_${exportStatus}_${date}.csv`;
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
      showNotification("CSV downloaded successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  };

  const tableColumns = [
    { key: "order_number", label: "Order #" },
    { key: "order_details", label: "Order Details", sortable: false, width: 50 },
    { key: "order_date" },
    { key: "restaurant" },
    { key: "guest_name" },
    { key: "discount_name" },
    { key: "discount_reason" },
    { key: "discount_comment" },
    { key: "discount_amount" },
    { key: "status" },
  ];

  const columns = tableColumns.map((column) => ({
    key: column.key,
    label:
      column.label ||
      COLUMN_LABELS[column.key] ||
      column.key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    sortable: column.sortable !== false,
    width: column.width,
  }));

  const rows = useMemo(
    () =>
      (classifiedDiscounts?.data || []).map((record) => ({
        order_number: {
          display: record?.order_no || "-",
          sortValue: record?.order_no,
        },
        order_details: (
          <OrderNumberCell
            orderValue={record?.order_guid}
            onView={handleOpen}
          />
        ),
        order_date: formatDate(record?.order_date, record?.time_zone_abbr),
        restaurant: record?.restaurant?.name || "-",
        guest_name: getGuestName(record?.network_guest),
        discount_name: record?.discount_name || "-",
        discount_reason: record?.discount_reason || "-",
        discount_comment: record?.discount_comment || "-",
        discount_amount: `$${Number(record?.discount_amount || 0).toFixed(2)}`,
        status: record?.status
          ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
          : "-",
      })),
    [classifiedDiscounts?.data],
  );

  const showTableLoader = isFetching && Boolean(classifiedDiscounts);

  if (!filtersHydrated && !classifiedDiscounts?.data?.length) {
    return <Loader />;
  }

  if (isFetching && !classifiedDiscounts) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: "hidden",
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color="#515152">Reports</StyledLink>
            <StyledLink color="#515152">Perq Reports</StyledLink>
            <StyledLink color="#515152">Classified Perqs</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <Box sx={filterContainerStyles}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1,
                alignItems: { xs: "flex-start", sm: "center" },
              }}
            >
              <Typography
                variant="body2"
                sx={{ ...viewTypographyStyles, alignSelf: "center" }}
              >
                View
              </Typography>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: "170px" } }}
              >
                <Select
                  value={filters.dateFilter}
                  renderValue={() => filters.dateFilter}
                  onChange={handleDateFilterChange}
                  sx={selectBaseStyles}
                  MenuProps={{ PaperProps: { sx: { width: 230 } } }}
                >
                  {CLASSIFIED_DISCOUNT_DATE_FILTERS.map((value) => (
                    <StyledMenuItem key={value} value={value}>
                      {filters.dateFilter === value ? (
                        <SelectedMenuItem data={value} />
                      ) : (
                        value
                      )}
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              {filters.dateFilter === "Custom Date" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" sx={viewTypographyStyles}>
                    from
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={normalizeDayjsValue(filters.customDateFrom)}
                      onChange={(value) =>
                        handleDateChange("customDateFrom", value)
                      }
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { ...datePickerStyles, minWidth: 140 },
                          inputProps: {
                            "data-testid": "classified-discounts-date-from",
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <Typography variant="body2" sx={viewTypographyStyles}>
                    through
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="MM-DD-YYYY"
                      value={normalizeDayjsValue(filters.customDateTo)}
                      onChange={(value) =>
                        handleDateChange("customDateTo", value)
                      }
                      slots={{ openPickerIcon: CalendarMonthIcon }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { ...datePickerStyles, minWidth: 140 },
                          inputProps: {
                            "data-testid": "classified-discounts-date-to",
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              ) : null}

              <Typography variant="body2" sx={viewTypographyStyles}>
                at
              </Typography>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: "205px" } }}
              >
                <Select
                  data-testid="classified-discounts-restaurant-group"
                  value={filters.restaurant_group_id}
                  renderValue={() => getRestaurantGroupName(filters.restaurant_group_id)}
                  onChange={handleRestaurantGroupChange}
                  onClose={() =>
                    setTimeout(() => setRestaurantGroupSearch(""), 250)
                  }
                  sx={selectBaseStyles}
                  endAdornment={
                    filters.restaurant_group_id !== ALL_FILTER_VALUE ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleRestaurantGroupClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={{ 
                    PaperProps: { 
                      ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantGroupSearch}
                    placeholder="Search restaurant group"
                    isFetching={restaurantGroupFetching}
                  />
                  {displayedRestaurantGroups.map((restaurantGroup) => (
                    <StyledMenuItem
                      key={restaurantGroup.label}
                      value={restaurantGroup.label}
                    >
                      {filters.restaurant_group_id === restaurantGroup.label ? (
                        <SelectedMenuItem data={restaurantGroup.value} />
                      ) : (
                        restaurantGroup.value
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: "220px" } }}
              >
                <Select
                  data-testid="classified-discounts-restaurant"
                  multiple
                  value={filters.restaurant_ids}
                  renderValue={() => getRestaurantDisplayValue()}
                  onChange={handleRestaurantChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
                  sx={selectBaseStyles}
                  endAdornment={
                    !filters.restaurant_ids.includes(ALL_FILTER_VALUE) ? (
                      <InputAdornment
                        position="end"
                        sx={{ position: "absolute", right: 25 }}
                      >
                        <IconButton
                          edge="end"
                          size="small"
                          disableRipple
                          onClick={handleRestaurantClear}
                        >
                          <ClearIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                  MenuProps={{
                    PaperProps: { 
                      ref: restaurantMenuRef, sx: { maxHeight: "40vh" },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                >
                  <DropdownSearchBar
                    changeSearchText={setRestaurantSearch}
                    placeholder="Search restaurant"
                    isFetching={restaurantFetching}
                  />
                  {displayedRestaurants.map((restaurant) => (
                    <StyledMenuItem key={restaurant.id} value={restaurant.id}>
                      {filters.restaurant_ids.includes(restaurant.id) ? (
                        <SelectedMenuItem data={restaurant.name} />
                      ) : (
                        restaurant.name
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <Typography variant="body2" sx={viewTypographyStyles}>
                for
              </Typography>

              <FormControl
                size="small"
                sx={{ minWidth: { xs: "100%", sm: "150px" } }}
              >
                <Select
                  value={status}
                  renderValue={() => statusLabel}
                  sx={selectBaseStyles}
                  data-testid="classified-discounts-status"
                  disabled
                >
                  <StyledMenuItem value="managed">
                    {STATUS_LABELS.managed}
                  </StyledMenuItem>
                  <StyledMenuItem value="unmanaged">
                    {STATUS_LABELS.unmanaged}
                  </StyledMenuItem>
                  <StyledMenuItem value="unclassified">
                    {STATUS_LABELS.unclassified}
                  </StyledMenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <ResetButton onClick={handleReset} />
              <UpdateButton
                onClick={handleApplyFilters}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </Box>

        <MenuHeading report={false}>
          {statusLabel} Perq Records
        </MenuHeading>

        <Box sx={{ px: 2, pb: 2 }}>
          {showTableLoader ? (
            <Box data-testid="classified-discounts-table-loader">
              <Loader />
            </Box>
          ) : (
            <IndexTable
              report={false}
              columns={columns}
              data={rows}
              type="entries"
              tableId="classified-discount-records"
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={classifiedDiscounts?.pageCount || 0}
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              showDownloadButton={true}
              disableDownloadButton={
                !classifiedDiscounts?.data?.length || isFetching || csvLoading
              }
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
            />
          )}
        </Box>
      </Box>

      <OrderAppliedDiscountModal
        open={orderViewOpen}
        handleClose={handleCloseOrderModal}
        ordersData={orderData}
        isLoading={orderDataLoading}
      />
    </React.Fragment>
  );
};
